import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { programmes } from "@/data/programmes";
import { trackEvent } from "@/lib/analytics";
import { usePageMetadata } from "@/hooks/use-page-metadata";

const HomeInteractiveSections = lazy(
  () => import("@/components/HomeInteractiveSections"),
);

const mainProgramme = programmes[0];
const revolutMentorshipCheckoutUrl =
  "https://checkout.revolut.com/pay/93d9940b-0f35-4ae5-87ae-42c1d1ff95f6";

function DeferredHomeInteractiveSections() {
  const sentinel = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(
    () =>
      window.location.hash === "#portfolio" ||
      window.location.hash === "#reviews",
  );

  useEffect(() => {
    const loadForSectionHash = () => {
      if (
        window.location.hash === "#portfolio" ||
        window.location.hash === "#reviews"
      ) {
        setShouldLoad(true);
      }
    };

    window.addEventListener("hashchange", loadForSectionHash);

    if (shouldLoad) {
      return () => window.removeEventListener("hashchange", loadForSectionHash);
    }

    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return () => window.removeEventListener("hashchange", loadForSectionHash);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "800px 0px" },
    );

    if (sentinel.current) observer.observe(sentinel.current);
    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", loadForSectionHash);
    };
  }, [shouldLoad]);

  return (
    <div ref={sentinel}>
      {shouldLoad ? (
        <Suspense
          fallback={
            <div
              className="flex min-h-96 items-center justify-center bg-background text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              Loading more content…
            </div>
          }
        >
          <HomeInteractiveSections />
        </Suspense>
      ) : (
        <div className="min-h-[48rem] bg-muted/30" aria-hidden="true" />
      )}
    </div>
  );
}

export default function Home() {
  usePageMetadata(
    "AI & Digital Creation Mentorship | FISL",
    "Practical AI mentorship and digital-skills programmes from Future Intelligence Systems Ltd."
  );

  const handleConsultation = (source: string) => {
    trackEvent("consultation_click", { source });
    window.open("https://calendar.app.google/q7DbU8wvN6oR8h918", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20">
      <Navbar variant="dark" onOpenConsultation={() => handleConsultation("navbar")} />

      <main>
        {/* SECTION 1: HERO */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-background text-foreground overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-grid-pattern pointer-events-none" />
          <div className="absolute top-1/4 -left-64 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-1/4 -right-64 w-[500px] h-[500px] bg-secondary/20 rounded-full blur-[120px] pointer-events-none" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-yellow-400/25 blur-2xl scale-150 animate-pulse" />
                <img
                  src={`${import.meta.env.BASE_URL}images/fisl-mark-288.webp`}
                  alt="Future Intelligence Systems Ltd Logo"
                  width={144}
                  height={144}
                  fetchPriority="high"
                  className="relative w-28 h-28 md:w-36 md:h-36 object-contain drop-shadow-[0_0_30px_rgba(212,160,23,0.7)]"
                />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-extrabold tracking-tight mb-6 leading-tight text-white">
              {mainProgramme.heroHeadline}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {mainProgramme.heroSubheadline}
            </p>

            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={() => handleConsultation("hero")}
                size="lg"
                className="h-14 px-8 text-lg font-bold rounded-full w-full sm:w-auto shadow-[0_0_20px_rgba(212,160,23,0.4)] hover:shadow-[0_0_30px_rgba(212,160,23,0.7)]"
                data-testid="button-hero-consultation"
              >
                Book Your Free Consultation
              </Button>
              <p className="text-sm text-muted-foreground font-medium">
                Choose a time that works for you.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROOF BAR */}
        <section className="bg-card border-y border-white/10 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 items-center text-center">
              <div>
                <p className="text-3xl font-display font-bold text-foreground">100+</p>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Clients Served</p>
              </div>
              <div className="hidden sm:block w-px h-10 bg-border" />
              <div>
                <p className="text-3xl font-display font-bold text-foreground">50+</p>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">AI Projects</p>
              </div>
              <div className="hidden sm:block w-px h-10 bg-border" />
              <div>
                <p className="text-3xl font-display font-bold text-foreground">5+</p>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Years Experience</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: THE PROBLEM */}
        <section className="py-24 bg-muted/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
              Does this sound like you?
            </h2>
            <div className="space-y-6">
              {mainProgramme.problemBullets.map((problem, idx) => (
                <div key={idx} className="flex gap-4 p-6 glass-card rounded-2xl">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
                    <X className="w-4 h-4 text-destructive" aria-hidden="true" />
                  </div>
                  <p className="text-lg text-foreground/80 pt-1 leading-relaxed">{problem}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: THE OUTCOME */}
        <section className="py-24 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
              Imagine what happens when you solve this.
            </h2>
            <div className="space-y-6">
              {mainProgramme.outcomeBullets.map((outcome, idx) => (
                <div key={idx} className="flex gap-4 p-6 glass-card rounded-2xl border-primary/20">
                  <div className="flex-shrink-0">
                    <CheckCircle2 className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-lg text-foreground/90 pt-1 leading-relaxed font-medium">{outcome}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5: THE OFFER */}
        <section className="py-24 bg-[#090909] text-foreground border-y border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-primary font-bold tracking-widest uppercase mb-4 text-sm">The Primary Offer</p>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-8">
              {mainProgramme.title}
            </h2>

            <div className="glass-card text-foreground rounded-3xl p-8 md:p-12 text-left max-w-3xl mx-auto">
              <p className="text-xl font-medium mb-2 text-foreground">{mainProgramme.description}</p>
              <p className="text-muted-foreground mb-8">Perfect for {mainProgramme.audience}.</p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {mainProgramme.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="font-semibold">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-border">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Investment</p>
                  <p className="text-3xl font-display font-bold">Book to discuss</p>
                </div>
                <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-end">
                  <Button
                    onClick={() => handleConsultation("offer_card")}
                    size="lg"
                    className="h-14 px-8 w-full font-bold rounded-full sm:w-auto"
                    data-testid="button-offer-consultation"
                  >
                    Book Your Free Consultation
                  </Button>
                  <a
                    href={revolutMentorshipCheckoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-11 items-center justify-center rounded-full border border-primary/40 bg-primary/5 px-5 text-sm font-semibold text-primary transition-all hover:border-primary hover:bg-primary/15 hover:shadow-[0_0_18px_rgba(212,160,23,0.25)]"
                    data-testid="link-revolut-mentorship-payment"
                  >
                    Pay for Mentorship via Revolut
                  </a>
                  <p className="text-center text-xs text-muted-foreground sm:text-right">
                    Continue to Revolut&apos;s hosted checkout
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: THE MECHANISM */}
        <section className="py-24 bg-background">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                A Practical Four-Step Learning Path
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Move from AI foundations into practical creation, building and automation.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {mainProgramme.steps.map((step, idx) => (
                <div key={idx} className="relative">
                  {idx < mainProgramme.steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-px bg-border -z-10" />
                  )}
                  <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold font-display mx-auto mb-6 shadow-[0_0_20px_rgba(212,160,23,0.3)] border-4 border-background">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold text-center mb-3">{step.title}</h3>
                  <p className="text-muted-foreground text-center text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DeferredHomeInteractiveSections />
      </main>

      <Footer />
    </div>
  );
}
